import { useRouter } from 'next/router'
import pluralize from 'pluralize'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useKey } from 'react-use'

import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import {
  useTrackPageView,
  useTrackSearchResults,
} from '../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { visualFiltersSection, visualFiltersSectionV2 } from '../../../../brunswick/__fixtures__'
import { useCollectionFilterQueryParamsContext } from '../../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { deriveMapFromList, disableScroll, enableScroll } from '../../../../lib/utils'
import { AnalyticsContext } from '../../../../types/figs'
import { ProductColor } from '../../../../types/graphql'
import { ExposedMethods } from '../Search/SearchOverlayForm/useViewModel'

import { useSearchPageQuery } from './hooks/useSearchPageQuery'
import { Props, ViewModel } from './types'

export function useViewModel({
  error,
  query,
  resultsChangedTimestamp,
  statusCode,
}: Props): ViewModel {
  useTrackPageView({
    pageViewHandle: 'search',
    pageViewCategory: 'search',
    searchQuery: query,
  })

  const { selectedFilters } = useCollectionFilterQueryParamsContext()
  const { trackSearchResults } = useTrackSearchResults()
  const selectedItemFilters = useMemo(() => {
    const selectedFilterKeys = Object.keys(selectedFilters)
    return selectedFilterKeys.map(key => ({
      key,
      values: selectedFilters[key]!,
    }))
  }, [selectedFilters])

  const {
    colors,
    fetchMoreSearchResults,
    searchFilters,
    searchNumItems,
    searchPageInfo,
    searchProducts,
    searchSuggestions,
    status,
  } = useSearchPageQuery({ query, selectedFilters: selectedItemFilters })

  const hasMoreItems = searchPageInfo?.hasNextPage
  const loadMoreItems = useCallback(async () => {
    await fetchMoreSearchResults({
      variables: {
        after: searchPageInfo?.endCursor,
      },
    })
  }, [fetchMoreSearchResults, searchPageInfo?.endCursor])

  const router = useRouter()
  const {
    magnolia: {
      searchPage: { relatedSearchesTitle, resultNoun },
    },
  } = useTranslation()

  const pageTitle = query ? `${query.toUpperCase()}` : undefined

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [autoCompleteOverlayVisible, setAutoCompleteOverlayVisible] = useState<boolean>(false)

  const searchOverlayFormRef = useRef<ExposedMethods>()

  const onAutoComplete = useCallback((hasResults: boolean) => {
    if (hasResults) setAutoCompleteOverlayVisible(true)
  }, [])

  const hideAutoCompleteOverlay = useCallback(() => {
    setAutoCompleteOverlayVisible(false)
    searchOverlayFormRef.current?.clearSearchResults()
  }, [])

  useEffect(() => {
    if (autoCompleteOverlayVisible) {
      disableScroll()
    } else {
      enableScroll()
    }
  }, [autoCompleteOverlayVisible])

  useKey(
    'Escape',
    () => {
      if (autoCompleteOverlayVisible) {
        hideAutoCompleteOverlay()
      }
    },
    undefined,
    [autoCompleteOverlayVisible, hideAutoCompleteOverlay]
  )

  const submitForm = useCallback((searchText: string) => {
    setIsLoading(true)
    hideAutoCompleteOverlay()
    router.push({
      pathname: '/search',
      query: { q: searchText },
    })
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (error) {
      reportClientError({
        error,
        context: {
          scope: 'Search Page',
          label: 'useViewModel error from GIP',
        },
      })
    }
  }, [error])

  useEffect(() => {
    setIsLoading(false)
  }, [resultsChangedTimestamp])

  const analyticsContext: AnalyticsContext = useMemo(
    () => ({
      category: 'Search Page',
      list: query,
    }),
    [query]
  )

  const numberOfResultsText: string | undefined =
    searchNumItems !== 0 ? `${searchNumItems} ${pluralize(resultNoun, searchNumItems)}` : undefined

  const substituteSearchTerm = useCallback(
    (nostoTitle: string | undefined) => nostoTitle?.replace('$searchTerm', query),
    [query]
  )

  const colorsByName: Record<string, ProductColor> = useMemo(() => {
    return deriveMapFromList(colors, o => o.name)
  }, [colors])

  const searchQueryTracker = useRef('')
  useEffect(() => {
    if (status === 'resolved' && searchQueryTracker.current !== query) {
      trackSearchResults({
        id: `page_`,
        query: query,
        searchType: 'page',
        topResults: searchProducts.map(product => {
          return { handle: product.handle || '' }
        }),
        totalResults: searchProducts.length,
      })
      searchQueryTracker.current = query
    }
  }, [trackSearchResults, searchProducts, query, status])

  return {
    analyticsContext,
    autoCompleteOverlayVisible,
    colorsByName,
    filters: searchFilters,
    hasMoreItems,
    hideAutoCompleteOverlay,
    initialSearchText: query,
    isLoading,
    loadMoreItems,
    totalItemsCount: searchNumItems,
    numberOfResultsText,
    onAutoComplete,
    pageTitle,
    products: searchProducts,
    relatedSearches: searchSuggestions,
    relatedSearchesTitle,
    searchOverlayFormRef,
    statusCode,
    submitForm,
    substituteSearchTerm,
    visualFiltersSection,
    visualFiltersSectionV2,
  }
}
